// Hooks
import { useStyles } from './styles/Users.styles';
import { useDeferredValue, useMemo, useCallback } from 'react';
import { useScrollablePagination } from '@hooks/useScrollablePagination';
import { useListUsersQuery } from '@features/redux/apis/auth';
import { useNameMapQuery } from '@features/redux/apis/org';
import { useAlert } from '@hooks/useAlert';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { useSelector } from 'react-redux';

// Base Components
import { ScrollToTop } from '@components/ScrollToTop';
import { QueryEffect } from '@helpers/QueryEffect';
import { Box, Typography, Pagination, useMediaQuery, useTheme, IconButton, OutlinedInput, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';

// Fixed Scope Components
import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';
import { PERMISSIONS } from '@consts/user';
import { UserActions } from './UserActions';
import { MobileUserCard, MobileUserCardSkeleton } from './MobileUserCard';

// Design system
import { colors, radii } from '@components/design-system/tokens';
import { DataTable, TableCard, Button } from '@components/design-system';
import type { DataTableColumn } from '@components/design-system/components/Table/DataTable';

// Types
import type { User } from '@features/redux/apis/auth';
import type { RootState } from '@features/redux/store';

// Consts
import { AppRoutes } from '@consts/routes';

const PAGE_SIZE = 50;
const MOBILE_PAGE_SIZE = 12;

export const Users = () => {
    const classes = useStyles();
    const { setAlert } = useAlert();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const role = useSelector((state: RootState) => state.auth.Roles.role);

    const { data: { companies = {}, resellers = {}, locations = {} } = {} } = useNameMapQuery({});

    const infoState = useLocalStorage({
        key: 'user-search-info',
        value: { search: '', page: 1 },
    });
    const [searchInfo, setSearchInfo] = infoState;
    const [, , clearInfo] = infoState;
    const { scrollContainerRef, scrollToTop } = useScrollablePagination();

    const handlePageChange = useCallback(
        (newPage: number) => {
            setSearchInfo((prev) => ({ ...prev, page: newPage }));
            scrollToTop();
        },
        [setSearchInfo, scrollToTop]
    );

    const pageSize = isMobile ? MOBILE_PAGE_SIZE : PAGE_SIZE;

    const queryState = useListUsersQuery({
        search: useDeferredValue(searchInfo.search),
        page: useDeferredValue(searchInfo.page),
        limit: pageSize,
    });
    const {
        data = {
            data: [],
            total: 0,
            limit: pageSize,
            page: {
                curr: 1,
                next: null,
                prev: null,
            },
        },
        isLoading,
    } = queryState;

    const { data: users, total } = data;

    const totalPages = Math.ceil(total / pageSize);
    const infoText =
        total > 0
            ? `${(searchInfo.page - 1) * pageSize + 1}-${Math.min(searchInfo.page * pageSize, total)} of ${total} users`
            : 'No users found';

    // Design system columns for desktop
    const designColumns = useMemo<DataTableColumn<User>[]>(() => {
        return [
            {
                field: 'given_name',
                headerName: 'First Name',
                width: 120,
            },
            {
                field: 'family_name',
                headerName: 'Last Name',
                width: 120,
            },
            {
                field: 'email',
                headerName: 'Email',
                width: 220,
            },
            {
                field: 'custom:Companies',
                headerName: 'Companies',
                width: 180,
                renderCell: ({ row }) => {
                    const ids = row['custom:Companies']?.split(',').filter(Boolean) || [];
                    if (ids.length === 0)
                        return (
                            <Typography variant='body2' color='text.secondary'>
                                —
                            </Typography>
                        );
                    const names = ids.slice(0, 2).map((id) => companies[id] || `#${id}`);
                    return (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {names.map((name, i) => (
                                <Typography key={i} variant='body2' sx={{ fontSize: '0.8125rem' }}>
                                    {name}
                                    {i < names.length - 1 ? ',' : ''}
                                    {ids.length > 2 && i === names.length - 1 ? ` +${ids.length - 2}` : ''}
                                </Typography>
                            ))}
                        </Box>
                    );
                },
            },
            {
                field: 'custom:Resellers',
                headerName: 'Resellers',
                width: 180,
                renderCell: ({ row }) => {
                    const ids = row['custom:Resellers']?.split(',').filter(Boolean) || [];
                    if (ids.length === 0)
                        return (
                            <Typography variant='body2' color='text.secondary'>
                                —
                            </Typography>
                        );
                    const names = ids.slice(0, 2).map((id) => resellers[id] || `#${id}`);
                    return (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {names.map((name, i) => (
                                <Typography key={i} variant='body2' sx={{ fontSize: '0.8125rem' }}>
                                    {name}
                                    {i < names.length - 1 ? ',' : ''}
                                    {ids.length > 2 && i === names.length - 1 ? ` +${ids.length - 2}` : ''}
                                </Typography>
                            ))}
                        </Box>
                    );
                },
            },
            {
                field: 'custom:Locations',
                headerName: 'Locations',
                width: 180,
                renderCell: ({ row }) => {
                    const ids = row['custom:Locations']?.split(',').filter(Boolean) || [];
                    if (ids.length === 0)
                        return (
                            <Typography variant='body2' color='text.secondary'>
                                —
                            </Typography>
                        );
                    const names = ids.slice(0, 2).map((id) => locations[id] || `#${id}`);
                    return (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {names.map((name, i) => (
                                <Typography key={i} variant='body2' sx={{ fontSize: '0.8125rem' }}>
                                    {name}
                                    {i < names.length - 1 ? ',' : ''}
                                    {ids.length > 2 && i === names.length - 1 ? ` +${ids.length - 2}` : ''}
                                </Typography>
                            ))}
                        </Box>
                    );
                },
            },
            {
                field: 'custom:userType',
                headerName: 'Permissions',
                width: 180,
                renderCell: ({ row }) => {
                    const perms = PERMISSIONS.reduce((acc, { key, label }) => {
                        if (+(row[key] || 0) > 0) acc.push(label);
                        return acc;
                    }, [] as string[]);
                    if (perms.length === 0)
                        return (
                            <Typography variant='body2' color='text.secondary'>
                                —
                            </Typography>
                        );
                    return (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {perms.slice(0, 2).map((perm, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: '4px',
                                        backgroundColor: colors.primary[50],
                                        color: colors.primary[700],
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                    }}>
                                    {perm}
                                </Box>
                            ))}
                            {perms.length > 2 && (
                                <Typography variant='body2' sx={{ fontSize: '0.75rem', color: colors.neutral[500] }}>
                                    +{perms.length - 2}
                                </Typography>
                            )}
                        </Box>
                    );
                },
            },
            ...(role.isAdmin
                ? [
                      {
                          field: 'custom:Notes' as keyof User,
                          headerName: 'Notes',
                          width: 150,
                      },
                  ]
                : []),
            {
                field: 'sub',
                headerName: 'Actions',
                width: 80,
                renderCell: ({ row }) => <UserActions user={row} />,
            },
        ];
    }, [companies, resellers, locations, role.isAdmin]);

    // Mobile View
    if (isMobile) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                }}>
                <QueryEffect
                    queryState={queryState}
                    effects={{
                        isError: () => {
                            setAlert('Failed to fetch users. Please reload and try again.', 'error');
                        },
                    }}
                />

                {/* Mobile Header - Row 1: Title + Add Button */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 2,
                        pt: 2,
                        pb: 1.5,
                        backgroundColor: colors.neutral[0],
                        borderBottom: `1px solid ${colors.neutral[200]}`,
                    }}>
                    <Typography
                        sx={{
                            fontSize: '1.125rem',
                            fontWeight: 700,
                            color: colors.neutral[900],
                        }}>
                        Users
                    </Typography>
                    <Link component={RouterLink} to={AppRoutes.ManageUser} sx={{ textDecoration: 'none' }}>
                        <Button variant='primary' size='small' startIcon={<AddIcon />}>
                            Add New User
                        </Button>
                    </Link>
                </Box>

                {/* Mobile Header - Row 2: Search + Info */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2,
                        py: 1.5,
                        backgroundColor: colors.neutral[0],
                        borderBottom: `1px solid ${colors.neutral[200]}`,
                    }}>
                    <OutlinedInput
                        size='small'
                        placeholder='Search users...'
                        value={searchInfo.search}
                        onChange={(e) => setSearchInfo((prev) => ({ ...prev, search: e.target.value, page: 1 }))}
                        sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-input': {
                                py: 1,
                                fontSize: '0.875rem',
                            },
                        }}
                        endAdornment={
                            searchInfo.search && (
                                <IconButton size='small' onClick={clearInfo}>
                                    <ClearIcon fontSize='small' />
                                </IconButton>
                            )
                        }
                    />
                    <Typography
                        sx={{
                            fontSize: '0.8125rem',
                            color: colors.neutral[600],
                            whiteSpace: 'nowrap',
                        }}>
                        {total} users
                    </Typography>
                </Box>

                {/* Cards Container */}
                <Box
                    ref={scrollContainerRef}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        p: 1.5,
                        backgroundColor: colors.neutral[50],
                    }}>
                    {isLoading ? (
                        Array.from({ length: 5 }).map((_, i) => <MobileUserCardSkeleton key={i} />)
                    ) : users.length === 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                color: colors.neutral[500],
                            }}>
                            <Typography>No users found</Typography>
                        </Box>
                    ) : (
                        users
                            .filter((user) => user.sub)
                            .map((user) => (
                                <MobileUserCard
                                    key={`user-${user.sub}`}
                                    data={user}
                                    companies={companies}
                                    resellers={resellers}
                                    locations={locations}
                                />
                            ))
                    )}
                </Box>

                {/* Pagination - Fixed at bottom */}
                {totalPages > 1 && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            py: 1.5,
                            backgroundColor: colors.neutral[0],
                            borderTop: `1px solid ${colors.neutral[200]}`,
                        }}>
                        <Pagination
                            count={totalPages}
                            page={searchInfo.page}
                            onChange={(_, newPage) => handlePageChange(newPage)}
                            color='primary'
                            size='small'
                            siblingCount={0}
                            boundaryCount={1}
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    borderRadius: `${radii.md}px`,
                                    minWidth: 32,
                                },
                            }}
                        />
                    </Box>
                )}
            </Box>
        );
    }

    // Desktop View - Using design system components
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
            }}>
            <QueryEffect
                queryState={queryState}
                effects={{
                    isError: () => {
                        setAlert('Failed to fetch users. Please reload and try again.', 'error');
                    },
                }}
            />

            <ScrollToTop />
            <BreadcrumbSubBar />

            {/* Desktop Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    px: 3,
                    py: 2,
                    backgroundColor: colors.neutral[0],
                    borderBottom: `1px solid ${colors.neutral[200]}`,
                }}>
                <Typography
                    sx={{
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        color: colors.neutral[900],
                    }}>
                    Manage Users
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <OutlinedInput
                        size='small'
                        placeholder='Search users...'
                        value={searchInfo.search}
                        onChange={(e) => setSearchInfo((prev) => ({ ...prev, search: e.target.value, page: 1 }))}
                        sx={{
                            minWidth: 250,
                            '& .MuiOutlinedInput-input': {
                                py: 1,
                            },
                        }}
                        endAdornment={
                            searchInfo.search && (
                                <IconButton size='small' onClick={clearInfo}>
                                    <ClearIcon fontSize='small' />
                                </IconButton>
                            )
                        }
                    />
                    <Link component={RouterLink} to={AppRoutes.ManageUser} sx={{ textDecoration: 'none' }}>
                        <Button variant='primary' startIcon={<AddIcon />}>
                            Add New User
                        </Button>
                    </Link>
                </Box>
            </Box>

            {/* Table */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    p: 2,
                    backgroundColor: colors.neutral[100],
                }}>
                <TableCard
                    title='Users'
                    sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
                    <DataTable
                        rows={users.filter((u) => u.sub)}
                        columns={designColumns}
                        getRowId={(row) => `user-${row.sub}`}
                        totalRows={total}
                        page={searchInfo.page}
                        pageSize={pageSize}
                        onPageChange={(page) => setSearchInfo((prev) => ({ ...prev, page }))}
                        loading={isLoading}
                        height='100%'
                        infoText={infoText}
                    />
                </TableCard>
            </Box>
        </Box>
    );
};
